// Topic Analysis tab — Brand24-style topic cards with mentions/reach/SoV
// + sentiment-share bar per topic. Data clusters by narrative_tag.
// Phase 5 adds "More stats" drill-in modal: full mentions list for the
// selected narrative, paginated through bp_mentions with narrative filter.
import React, { useEffect, useState } from 'react';
import { bp_topics, bp_mentions } from '../../../services';
import './brand-mentions.scss';
import { formatNumber, timeAgo, unwrap, EmptyState, SectionHeader, Pill } from './_shared';

function TopicDetailsModal({ topic, days, onClose }) {
    const [mentions, setMentions] = useState([]);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(true);
    

    useEffect(() => {
        setLoading(true);
        bp_mentions({
            days,
            narrative: topic.narrative_tag,
            page,
            page_size: 25,
            sort: '-posted_at',
        }).then(r => {
            const d = unwrap(r) || {};
            setMentions(d.results || []);
            setPages(d.pages || 1);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [topic.narrative_tag, days, page]);

    return (
        <div onClick={onClose}
             style={{
                 position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
                 zIndex: 9000, display: 'flex', alignItems: 'flex-start',
                 justifyContent: 'center', padding: '40px 16px', overflow: 'auto',
             }}>
            <div onClick={e => e.stopPropagation()}
                 style={{
                     background: '#fff', borderRadius: 8, maxWidth: 900, width: '100%',
                     boxShadow: '0 20px 50px rgba(2,6,23,0.35)',
                     border: '1px solid #e2e8f0', padding: 24,
                 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    {topic.is_user_defined && topic.emoji && (
                        <span style={{ fontSize: 22 }}>{topic.emoji}</span>
                    )}
                    <div style={{ flex: 1 }}>
                        <h3 style={{ margin: 0, color: '#0f172a' }}>{topic.title}</h3>
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                            {formatNumber(topic.mentions)} mentions ·
                            reach {formatNumber(topic.total_reach)} ·
                            {topic.share_of_voice_pct}% share of voice ·
                            last {days} days
                        </div>
                    </div>
                    <button onClick={onClose} className="btn btn-sm btn-secondary">Close</button>
                </div>
                {topic.description && (
                    <p style={{ color: '#475569', fontSize: 13, marginTop: 0 }}>
                        {topic.description}
                    </p>
                )}

                <div style={{ display: 'grid', gap: 8, marginTop: 16, maxHeight: '60vh', overflowY: 'auto' }}>
                    {loading && <div style={{ color: '#94a3b8', fontSize: 12 }}>Loading mentions…</div>}
                    {!loading && mentions.length === 0 && (
                        <div className="bm_empty">No mentions in this window for this topic.</div>
                    )}
                    {!loading && mentions.map(m => (
                        <div key={m.id}
                             style={{
                                 background: '#f8fafc', borderRadius: 6, padding: '10px 12px',
                                 border: '1px solid #e2e8f0', fontSize: 12,
                             }}>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 4 }}>
                                <span style={{ fontWeight: 600, color: '#0f172a' }}>{m.handle || '(no handle)'}</span>
                                <span style={{ color: '#64748b' }}>· {m.platform}</span>
                                {m.followers > 0 && <span style={{ color: '#94a3b8' }}>· {formatNumber(m.followers)} followers</span>}
                                <span style={{
                                    marginLeft: 'auto',
                                    padding: '1px 6px',
                                    borderRadius: 3,
                                    fontSize: 10,
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    background: m.sentiment === 'positive' ? 'rgba(34,197,94,0.15)'
                                             : m.sentiment === 'negative' ? 'rgba(220,38,38,0.15)'
                                             : 'rgba(148,163,184,0.15)',
                                    color: m.sentiment === 'positive' ? '#15803d'
                                         : m.sentiment === 'negative' ? '#b91c1c'
                                         : '#475569',
                                }}>{m.sentiment || 'neutral'}</span>
                                <span style={{ fontSize: 10, color: '#94a3b8' }}>{timeAgo(m.posted_at)}</span>
                            </div>
                            <div style={{ color: '#334155', lineHeight: 1.5 }}>{m.text}</div>
                            {m.url && (
                                <a href={m.url} target="_blank" rel="noopener noreferrer"
                                   style={{ fontSize: 10, color: '#3b82f6', textDecoration: 'underline' }}>
                                    Open source
                                </a>
                            )}
                        </div>
                    ))}
                </div>

                {!loading && pages > 1 && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'center' }}>
                        <button onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="btn btn-sm btn-secondary">‹ Prev</button>
                        <span style={{ alignSelf: 'center', fontSize: 12, color: '#475569' }}>
                            Page {page} of {pages}
                        </span>
                        <button onClick={() => setPage(p => Math.min(pages, p + 1))}
                                disabled={page === pages}
                                className="btn btn-sm btn-secondary">Next ›</button>
                    </div>
                )}
            </div>
        </div>
    );
}

// Compact sparkline used on every topic card. 14-day mention volume with
// the daily peaks visible. SVG inline (no chart lib) so it stays tiny.
function TopicSparkline({ series, color = '#3b82f6' }) {
    if (!series || series.length === 0) return null;
    const w = 120, h = 28, pad = 1;
    const values = series.map(s => s.mentions || 0);
    const max = Math.max(1, ...values);
    const stepX = (w - 2 * pad) / Math.max(1, values.length - 1);
    const x = i => pad + i * stepX;
    const y = v => h - pad - (v / max) * (h - 2 * pad);
    const path = values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(v)}`).join(' ');
    const maxIdx = values.indexOf(max);
    return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
            <path d={path} fill="none" stroke={color} strokeWidth="1.4"
                  strokeLinejoin="round" strokeLinecap="round" />
            {/* highlight the peak so the cadence is readable at a glance */}
            <circle cx={x(maxIdx)} cy={y(max)} r="2.2" fill={color} />
        </svg>
    );
}

function DeltaChip({ pct }) {
    if (pct == null) return null;
    if (Math.abs(pct) < 0.5) return <span className="bm_topic_delta bm_topic_delta--flat">no change</span>;
    const up = pct > 0;
    return (
        <span className={`bm_topic_delta ${up ? 'bm_topic_delta--up' : 'bm_topic_delta--down'}`}>
            {up ? '▲' : '▼'} {Math.abs(pct).toFixed(0)}%
            <span className="bm_topic_delta_prior"> vs prior</span>
        </span>
    );
}

// "9d ago" / "3h ago" / "just now" — used by the empty-state hint on
// monitored topics that have no in-window matches but have history.
function relativeAge(iso) {
    if (!iso) return null;
    const diffMs = Date.now() - new Date(iso).getTime();
    if (diffMs < 0) return 'just now';
    const min = Math.floor(diffMs / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const days = Math.floor(hr / 24);
    if (days < 30) return `${days}d ago`;
    const mon = Math.floor(days / 30);
    if (mon < 12) return `${mon}mo ago`;
    return `${Math.floor(mon / 12)}y ago`;
}

function TopicCard({ topic, onDrillIn }) {
    const ss = topic.sentiment || {};
    const sum = (ss.positive || 0) + (ss.neutral || 0) + (ss.negative || 0);
    const pct = (n) => sum ? Math.round((n / sum) * 100) : 0;
    const sparkline = topic.sparkline || [];
    const sparkColor = topic.is_custom ? '#3b82f6' : '#7c3aed';
    const lastMatch = relativeAge(topic.last_match_at);
    const isEmpty = (topic.mentions || 0) === 0;

    return (
        <div className={`bm_topic_card ${topic.is_custom ? 'is-custom' : ''} ${isEmpty ? 'is-empty' : ''}`}
             style={topic.is_custom ? { borderLeft: '3px solid #3b82f6' } : undefined}>
            <div className="bm_topic_head">
                {topic.is_custom && topic.emoji && (
                    <span className="bm_topic_emoji">{topic.emoji}</span>
                )}
                <span className="bm_topic_title">{topic.title}</span>
                <DeltaChip pct={topic.delta_pct} />
                {sparkline.length > 0 && (
                    <span className="bm_topic_spark" title="Daily mention volume in this window">
                        <TopicSparkline series={sparkline} color={sparkColor} />
                    </span>
                )}
                <button onClick={() => onDrillIn(topic)}
                        className="btn btn-sm btn-secondary"
                        style={{ marginLeft: 'auto' }}>
                    View posts →
                </button>
            </div>
            <p className="bm_topic_desc">
                {topic.description}
                {topic.is_custom && topic.keywords && (
                    <span style={{ display: 'block', marginTop: 4, fontSize: 11, color: '#94a3b8' }}>
                        Keywords: {topic.keywords.join(' · ')}
                    </span>
                )}
            </p>
            {isEmpty ? (
                <div className="bm_topic_empty_state">
                    {lastMatch
                        ? <>Watching — last match <strong>{lastMatch}</strong>, nothing in this window. Try widening the date range.</>
                        : <>Watching — no matches captured yet for these keywords.</>}
                </div>
            ) : (
                <>
                    <div className="bm_topic_metrics">
                        <div className="bm_topic_metric">
                            <label>Mentions</label>
                            <span className="v">{formatNumber(topic.mentions)}</span>
                        </div>
                        <div className="bm_topic_metric">
                            <label>Reach</label>
                            <span className="v">{formatNumber(topic.total_reach)}</span>
                        </div>
                        <div className="bm_topic_metric">
                            <label>Share of Voice</label>
                            <span className="v">{topic.share_of_voice_pct}%</span>
                        </div>
                        {lastMatch && (
                            <div className="bm_topic_metric">
                                <label>Last match</label>
                                <span className="v" style={{ fontSize: 13 }}>{lastMatch}</span>
                            </div>
                        )}
                    </div>
                    {sum > 0 && (
                        <div className="bm_topic_sentiment">
                            <div className="bm_topic_sent_bar">
                                <span className="seg pos" style={{ width: pct(ss.positive) + '%' }} />
                                <span className="seg neu" style={{ width: pct(ss.neutral)  + '%' }} />
                                <span className="seg neg" style={{ width: pct(ss.negative) + '%' }} />
                            </div>
                            <div className="bm_topic_sent_legend">
                                <span>● {pct(ss.positive)}% positive</span>
                                <span>● {pct(ss.neutral)}% neutral</span>
                                <span>● {pct(ss.negative)}% negative</span>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default function TopicsTab() {
    const [days, setDays] = useState(14);
    const [sortBy, setSortBy] = useState('reach');
    const [topics, setTopics] = useState([]);
    const [userTopics, setUserTopics] = useState([]);
    const [total, setTotal] = useState(0);
    const [openTopic, setOpenTopic] = useState(null);

    const [totalAllTime, setTotalAllTime] = useState(null);
    useEffect(() => {
        bp_topics({ days }).then(r => {
            const d = unwrap(r) || {};
            const items = d.topics || [];
            const sorter = (a, b) =>
                sortBy === 'mentions' ? b.mentions - a.mentions
                : sortBy === 'sov' ? b.share_of_voice_pct - a.share_of_voice_pct
                : b.total_reach - a.total_reach;
            setTopics([...items].sort(sorter));
            setUserTopics(
                [...(d.user_topics || [])]
                    .map(t => ({ ...t, is_user_defined: true }))
                    .sort(sorter)
            );
            setTotal(d.total_mentions || 0);
            setTotalAllTime(typeof d.total_all_time_mentions === 'number'
                            ? d.total_all_time_mentions : null);
        }).catch(() => {});
    }, [days, sortBy]);

    return (
        <>
            <div className="bm_header">
                <div>
                    <div className="bm_subtitle">
                        Topic Analysis · {total} mentions across {topics.length} narratives
                        (last {days} days)
                        {totalAllTime != null && totalAllTime !== total && (
                            <span style={{
                                marginLeft: 8, fontSize: 12, color: '#64748b',
                                fontWeight: 400,
                            }}>
                                · {totalAllTime} captured all-time
                            </span>
                        )}
                        {totalAllTime != null && totalAllTime === total && total > 0 && (
                            <span style={{
                                marginLeft: 8, fontSize: 10, fontWeight: 700,
                                color: '#475569',
                                background: 'rgba(148, 163, 184, 0.12)',
                                border: '1px solid rgba(148, 163, 184, 0.3)',
                                padding: '2px 8px', borderRadius: 4,
                                letterSpacing: 0.4, textTransform: 'uppercase',
                            }} title={
                                `All ${total} captured mentions land inside this window. ` +
                                `Widening the lookback won't surface more topics — this org ` +
                                `has no older data than ${days} days.`
                            }>
                                All captured
                            </span>
                        )}
                    </div>
                    <div className="bm_subtitle" style={{ marginTop: 4, fontSize: 12 }}>
                        Discover the most important topics and trends in your brand
                        coverage. Check sentiment + reach + share-of-voice per topic.
                    </div>
                </div>
            </div>

            <div className="bm_filters">
                <select value={days} onChange={e => setDays(Number(e.target.value))}>
                    <option value={7}>Last 7 days</option>
                    <option value={14}>Last 14 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                </select>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                    <option value="reach">Reach: high to low</option>
                    <option value="mentions">Mentions: high to low</option>
                    <option value="sov">Share of Voice: high to low</option>
                </select>
            </div>

            {/* Click handler — fire two events: bm-tab-switch to navigate
                to Mentions, bm-mentions-set-filter to seed the feed filter
                with the topic's keywords (custom) or narrative_tag (auto).
                MentionsTab listens for the second event and applies it. */}
            {(() => null)()}

            {/* User-defined Monitored Topics (Settings → Monitored topics) */}
            {userTopics.length > 0 && (
                <>
                    <SectionHeader title="Your monitored topics" count={userTopics.length} />
                    <div className="bm_topics_list">
                        {userTopics.map(t => (
                            <TopicCard key={t.narrative_tag} topic={t}
                                       onDrillIn={topic => {
                                           window.dispatchEvent(new CustomEvent('bm-mentions-set-filter', {
                                               detail: { monitored_topic: topic.title },
                                           }));
                                           window.dispatchEvent(new CustomEvent('bm-tab-switch', {
                                               detail: { tab: 'mentions' },
                                           }));
                                       }} />
                        ))}
                    </div>
                </>
            )}

            <SectionHeader title="Auto-detected topics" count={topics.length}
                           style={{ marginTop: userTopics.length ? 16 : 0 }} />
            <div className="bm_topics_list">
                {topics.length === 0 && userTopics.length === 0 && (
                    <EmptyState
                        mark="bars"
                        title="No topics in this window"
                        body="Topics auto-cluster from the brand-mention scanner's narrative tags. Add custom topics in Settings → Monitored topics to track specific phrases (e.g. CEPI 3.0, Disease X, vaccine misinformation)."
                    />
                )}
                {topics.map(t => (
                    <TopicCard key={t.narrative_tag} topic={t}
                               onDrillIn={topic => {
                                   window.dispatchEvent(new CustomEvent('bm-mentions-set-filter', {
                                       detail: { narrative: topic.narrative_tag },
                                   }));
                                   window.dispatchEvent(new CustomEvent('bm-tab-switch', {
                                       detail: { tab: 'mentions' },
                                   }));
                               }} />
                ))}
                {openTopic && (
                    <TopicDetailsModal topic={openTopic} days={days}
                                       onClose={() => setOpenTopic(null)} />
                )}
            </div>
        </>
    );
}
